import type { ReactNode } from 'react';
import React from 'react';
import type { DroppableProvided, DropResult } from 'react-beautiful-dnd';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { ConstructionListRow } from '~/components/common/constructions/list/ConstructionListRow';
import type { ConstructionItem } from '~/models/ConstructionItem';
import type { NskResponse } from '~/models/Api';
import type {
  DealConstruction,
  DealConstructionContext,
  DealConstructionEdit
} from '~/models/DealConstruction';
import { ConstructionListFooterRow } from './ConstructionListFooterRow';
import { FeeRow } from './FeeRow';
import { ConstructionAmountRow } from './ConstructionAmountRow';
import type { Deal } from '~/models/Deal';
import type { DealEstimationHeader } from '~/models/DealEstimationHeader';

type Props = {
  readonly: boolean;
  deal: Deal;
  constructionContext: DealConstructionContext;
  requestHeader: DealEstimationHeader | null;
  constructionItems: ConstructionItem[];
  handleMutateConstructions: (() => Promise<void>) | null;
  handleUpdate:
    | ((
        submitValue: DealConstructionEdit,
        estimationId: number | null,
        historyId: number | null
      ) => Promise<NskResponse<void>>)
    | null;
  handleDestroy:
    | ((deleteConstruction: DealConstruction) => Promise<NskResponse<void>>)
    | null;
  handleUpdateSeq:
    | ((constructions: DealConstruction[]) => Promise<void>)
    | null;
  handleUpdateEnabled:
    | ((constructions: DealConstruction) => Promise<void>)
    | null;
  target: 'owner' | 'rentee' | null;
  companyType: 'client' | 'vendor' | 'partner';
  docType: 'billing' | 'estimation';
  showHighlight: boolean;
  showEstimationConfirmedButton?: boolean;
};

export const ConstructionListRows: React.FC<Props> = ({
  readonly,
  deal,
  constructionContext,
  requestHeader,
  constructionItems,
  handleMutateConstructions,
  handleUpdate,
  handleDestroy,
  handleUpdateSeq,
  handleUpdateEnabled,
  target,
  companyType,
  docType,
  showHighlight,
  showEstimationConfirmedButton = false
}: Props) => {
  const { dealConstructions } = constructionContext;

  // 並び替えしたときにmutate前に画面に反映するためのstate
  const [showConstructions, setShowConstructions] = React.useState<
    DealConstruction[]
  >([]);
  React.useEffect(() => {
    setShowConstructions(dealConstructions);
  }, [dealConstructions]);

  const renderRow = (
    dealConstruction: DealConstruction,
    index: number
  ): ReactNode => (
    <ConstructionListRow
      key={dealConstruction.id}
      constructionItems={constructionItems}
      deal={deal}
      dealConstruction={dealConstruction}
      handleDestroy={handleDestroy}
      handleMutateConstructions={handleMutateConstructions}
      handleUpdate={handleUpdate}
      handleUpdateEnabled={handleUpdateEnabled}
      handleUpdateSeq={handleUpdateSeq}
      index={index}
      readonly={readonly}
      requestHeader={requestHeader}
      showHighlight={showHighlight}
      target={target}
    />
  );
  const handleDragEnd = async (result: DropResult): Promise<void> => {
    const { destination, source, draggableId } = result;
    if (!destination) {
      return;
    }
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    const moveConstruction =
      dealConstructions.find(
        (e: DealConstruction) => String(e.id) === draggableId
      ) ?? null;
    const newConstruction: DealConstruction[] = [];
    const wkConstruction = [...dealConstructions];
    wkConstruction.splice(source.index, 1);
    if (moveConstruction !== null) {
      wkConstruction.splice(destination.index, 0, moveConstruction);
    }
    wkConstruction.forEach((e: DealConstruction, i: number) => {
      newConstruction.push({ ...e, seq: i + 1 });
    });
    setShowConstructions(newConstruction);
    await handleUpdateSeq?.(newConstruction);
    await handleMutateConstructions?.().then();
  };
  const showFeeContext =
    constructionContext.type === 'DealEstimationContext' ||
    constructionContext.type === 'DealEstimationHistoryContext';
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="droppable">
        {(provided: DroppableProvided): React.ReactElement<HTMLElement> => (
          <tbody ref={provided.innerRef} {...provided.droppableProps}>
            <>
              {showConstructions.map(renderRow)}
              {provided.placeholder}
              {dealConstructions.length > 0 &&
                showFeeContext &&
                constructionContext.managementFee !== 0 && (
                  <FeeRow
                    fee={constructionContext.managementFee}
                    feeTitle="諸経費（工事管理費）"
                    showSwitchEnable={handleUpdateEnabled !== null}
                  />
                )}
              {companyType === 'vendor' &&
                showFeeContext &&
                constructionContext.vendorFee !== 0 && (
                  <ConstructionAmountRow
                    constructionContext={constructionContext}
                  />
                )}
              {dealConstructions.length > 0 &&
                companyType !== 'client' &&
                showFeeContext &&
                constructionContext.vendorFee !== 0 && (
                  <FeeRow
                    fee={constructionContext.vendorFee}
                    feeTitle="送客手数料（管理会社には表示されません）"
                    showSwitchEnable={handleUpdateEnabled !== null}
                  />
                )}
              <ConstructionListFooterRow
                companyType={companyType}
                constructionContext={constructionContext}
                docType={docType}
                showButtons={!readonly}
                showEstimationConfirmedButton={showEstimationConfirmedButton}
                showSwitchEnable={handleUpdateEnabled !== null}
                target={target}
              />
            </>
          </tbody>
        )}
      </Droppable>
    </DragDropContext>
  );
};
